import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from '@/components/ui/use-toast';
import { useFeatureFlag } from '@/lib/featureFlags/useFeatureFlag';
import { usePermissionsStore } from '@/lib/stores/usePermissionsStore';

import {
  glacierPolicyApiAcknowledgePolicyAlertMutation,
  glacierPolicyApiGetChildPolicyOptions,
  glacierPolicyApiGetPolicyAlertsOptions,
  glacierPolicyApiGetPolicyAlertsQueryKey,
  glacierPolicyApiGetPolicyOptions,
  glacierPolicyApiGetPolicyQueryKey,
} from '@/lib/heyapi/@tanstack/react-query.gen';
import { glacierPolicyApiGetLatestTransactionSummary } from '@/lib/heyapi/sdk.gen';
import type { ChildPolicyData } from '@/lib/heyapi/types.gen';

import { AlertBanner } from './components/AlertBanner';
import { HeaderSkeleton } from './components/HeaderSkeleton';
import { HeaderView } from './components/HeaderView';
import { formatErrorMessage } from './utils';

export const Header = () => {
  const { id } = useParams();
  const isTimeTraveling = usePermissionsStore((state) => state.isTimeTraveling);
  const showRenewalFeatures = useFeatureFlag('ENABLE_RENEWAL_FEATURES');
  const queryClient = useQueryClient();
  const [showAcknowledgeDialog, setShowAcknowledgeDialog] = useState(false);
  const [selectedAlertId, setSelectedAlertId] = useState<number | null>(null);
  const [renewalPolicyData, setRenewalPolicyData] =
    useState<ChildPolicyData | null>(null);

  const { data: renewalData, isError: isRenewalError } = useQuery({
    ...glacierPolicyApiGetChildPolicyOptions({
      path: {
        account_id: id!,
      },
    }),
    retry: false,
  });

  useEffect(() => {
    if (renewalData && !isRenewalError) {
      setRenewalPolicyData(renewalData);
    }
    if (isRenewalError) {
      setRenewalPolicyData(null);
    }
  }, [renewalData, isRenewalError]);

  const { data } = useQuery({
    ...glacierPolicyApiGetPolicyOptions({
      path: {
        account_id: id!,
      },
    }),
  });

  const policyQK = glacierPolicyApiGetPolicyQueryKey({
    path: {
      account_id: id!,
    },
  });

  const summaryResponse = useQuery({
    queryKey: ['latestTransactionSummary', id],
    queryFn: () =>
      glacierPolicyApiGetLatestTransactionSummary({
        path: {
          account_id: id!,
        },
      }),
  });

  const { data: alertsData } = useQuery({
    ...glacierPolicyApiGetPolicyAlertsOptions({
      path: {
        account_id: id!,
      },
    }),
  });

  const AlertsQK = glacierPolicyApiGetPolicyAlertsQueryKey({
    path: {
      account_id: id!,
    },
  });

  // determine if the banner should be shown based on the alerts endpoint
  const shouldShowAlertBanners = useMemo(() => {
    if (isTimeTraveling) return false;

    return (alertsData?.length ?? 0) > 0 && showRenewalFeatures;
  }, [alertsData, isTimeTraveling, showRenewalFeatures]);

  const { mutate: acknowledgeAlert, isPending: isAcknowledging } = useMutation({
    ...glacierPolicyApiAcknowledgePolicyAlertMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: policyQK });
      queryClient.invalidateQueries({ queryKey: AlertsQK });
      setShowAcknowledgeDialog(false);
      setSelectedAlertId(null);
      toast({
        title: 'Alert Acknowledged',
        description: (
          <pre className="mt-2 w-[340px] rounded-md bg-slate-950 p-4">
            <code className="text-white">Alert acknowledged successfully</code>
          </pre>
        ),
      });
    },
    onError: (error) => {
      setShowAcknowledgeDialog(false);
      setSelectedAlertId(null);
      toast({
        title: 'Error Acknowledging Alert',
        description: formatErrorMessage(
          error,
          'An error occurred while acknowledging the alert'
        ),
      });
    },
  });

  const handleConfirmAcknowledge = () => {
    acknowledgeAlert({
      path: {
        account_id: id!,
        alert_id: selectedAlertId!,
      },
    });
  };

  if (!data) {
    return <HeaderSkeleton />;
  }

  return (
    <div>
      <HeaderView
        renewalPolicyData={renewalPolicyData}
        policy={data}
        latestTransactionSummary={summaryResponse.data?.data}
        hasAlerts={shouldShowAlertBanners}
      />
      {shouldShowAlertBanners
        ? alertsData?.map((alert) => (
            <AlertBanner
              policyId={id!}
              isPending={isAcknowledging}
              selectedAlertId={selectedAlertId}
              key={alert.id}
              alert={alert}
              setShowAcknowledgeDialog={setShowAcknowledgeDialog}
              setSelectedAlertId={setSelectedAlertId}
            />
          ))
        : null}

      <Dialog
        open={showAcknowledgeDialog}
        onOpenChange={setShowAcknowledgeDialog}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Acknowledge Policy Alert</DialogTitle>
            <DialogDescription>
              Are you sure you want to acknowledge this policy? This action will
              permanently dismiss the alert.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowAcknowledgeDialog(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleConfirmAcknowledge}>Acknowledge</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
